(() => {
  const MESSAGE = 'This product is already in your cart.';
  const MUTATION = /\/cart\/(add|change|update)\.js$/;
  const INPUTS = [
    'product-form input[name="quantity"]',
    'cart-items input[name="updates[]"]',
    'cart-drawer-items input[name="updates[]"]',
    '#essential-cart-drawer-portal input[type="number"]'
  ].join(',');
  const id = (value) => value == null ? '' : String(value);

  function ownedProductHandles(root = document) {
    const manifest = root.querySelector && root.querySelector('#DgmOwnedProductHandles');
    if (!manifest) return new Set();
    try {
      const handles = JSON.parse(manifest.textContent || '[]');
      return new Set(Array.isArray(handles) ? handles.map((handle) => String(handle).trim().toLowerCase()).filter(Boolean) : []);
    } catch (error) {
      console.warn('Owned-product recommendations could not be initialized.', error);
      return new Set();
    }
  }

  function productHandleFromCard(card) {
    const link = card && card.querySelector && card.querySelector(
      '[data-essential-cart-element="upsell-product-title"][href*="/products/"], ' +
      '[data-essential-cart-element="upsell-image-container"][href*="/products/"]'
    );
    if (!link) return '';
    try {
      const parts = new URL(link.getAttribute('href'), window.location.origin).pathname.split('/products/');
      return decodeURIComponent((parts[1] || '').split('/')[0]).trim().toLowerCase();
    } catch (error) {
      return '';
    }
  }

  function uniqueUpdates(cart) {
    const updates = {};
    const products = new Set();
    if (!cart || !Array.isArray(cart.items)) return updates;
    cart.items.forEach((item) => {
      const productId = id(item.product_id);
      const key = item.key || id(item.variant_id || item.id);
      if (!key || Number(item.quantity || 0) <= 0) return;
      if (productId && products.has(productId)) {
        updates[key] = 0;
        return;
      }
      if (productId) products.add(productId);
      if (Number(item.quantity || 0) > 1) updates[key] = 1;
    });
    return updates;
  }

  class SingleProductCart {
    constructor() {
      this.message = MESSAGE;
      this.cart = null;
      this.variantProducts = new Map();
      this.originalFetch = window.fetch.bind(window);
      this.timer = null;
      this.pending = null;
      this.internal = false;
      this.essentialBound = false;
      this.ownedProducts = ownedProductHandles();
      this.recommendationFilterPending = false;
      this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
      this.enforceInputs(document);
      this.hideCartQuantityControls(document);
      this.filterOwnedRecommendations();
      this.observeInputs();
      document.addEventListener('click', (event) => this.onClick(event), true);
      document.addEventListener('change', (event) => this.onChange(event), true);
      document.addEventListener('afterCartChanged', (event) => this.onCartChanged(event));
      document.addEventListener('afterUpdateQuantity', (event) => this.onCartChanged(event));
      if (!this.designMode) {
        this.observeRequests();
        this.bindEssential();
        this.reconcile();
      }
    }

    url(path) {
      const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
      return `${root}${path}`.replace(/\/+/g, '/');
    }

    async json(url, options) {
      const response = await this.originalFetch(url, options);
      const data = await response.json();
      if (!response.ok || data && data.errors) {
        throw new Error(data && (data.description || data.message) || MESSAGE);
      }
      return data;
    }

    remember(cart) {
      if (!cart || !Array.isArray(cart.items)) return cart;
      this.cart = cart;
      cart.items.forEach((item) => {
        const variantId = id(item.variant_id || item.id);
        const productId = id(item.product_id);
        if (variantId && productId) this.variantProducts.set(variantId, productId);
      });
      return cart;
    }

    async fetchCart() {
      return this.remember(await this.json(this.url('cart.js'), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }));
    }

    async productId(variantId) {
      const variant = id(variantId);
      if (!variant) return '';
      if (this.variantProducts.has(variant)) return this.variantProducts.get(variant);
      try {
        const data = await this.json(this.url(`variants/${encodeURIComponent(variant)}.js`), {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        const product = id(data.product_id);
        if (product) this.variantProducts.set(variant, product);
        return product;
      } catch (error) {
        console.warn('The product limit could not resolve the selected variant.', error);
        return '';
      }
    }

    async canAdd(formData) {
      if (this.designMode) return true;
      const variant = id(formData && formData.get && formData.get('id'));
      const quantity = Number(formData && formData.get && (formData.get('quantity') || 1));
      if (!variant || quantity <= 0) return true;
      const cart = await this.fetchCart().catch(() => this.cart);
      if (!cart || !Array.isArray(cart.items)) return true;
      const product = await this.productId(variant);
      return !cart.items.some((item) => Number(item.quantity || 0) > 0 && (
        product ? id(item.product_id) === product : id(item.variant_id || item.id) === variant
      ));
    }

    observeRequests() {
      const controller = this;
      window.fetch = function (...args) {
        const requestUrl = typeof args[0] === 'string' ? args[0] : args[0] && args[0].url || '';
        let pathname = '';
        try { pathname = new URL(requestUrl, window.location.origin).pathname; } catch (error) {}
        return controller.originalFetch(...args).then((response) => {
          if (!controller.internal && MUTATION.test(pathname)) controller.schedule();
          return response;
        });
      };
    }

    schedule() {
      if (this.designMode || this.timer) return;
      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.reconcile();
      }, 75);
    }

    async reconcile() {
      if (this.designMode) return null;
      if (this.pending) return this.pending;
      this.pending = (async () => {
        const cart = await this.fetchCart();
        const updates = uniqueUpdates(cart);
        if (!Object.keys(updates).length) return cart;
        this.internal = true;
        try {
          const updated = await this.json(this.url('cart/update.js'), {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              updates,
              sections: ['cart-drawer', 'cart-icon-bubble', 'main-cart-items', 'main-cart-footer'],
              sections_url: window.location.pathname
            })
          });
          this.remember(updated);
          this.refresh(updated);
          this.notify();
          return updated;
        } finally {
          this.internal = false;
        }
      })().catch((error) => {
        console.error('The one-per-product cart limit could not be enforced.', error);
        return null;
      }).finally(() => {
        this.pending = null;
        this.enforceInputs(document);
      });
      return this.pending;
    }

    refresh(cart) {
      const essential = window.essentialCartApi;
      if (essential && typeof essential.refreshCart === 'function') essential.refreshCart();
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function' && cart.sections && cart.sections['cart-drawer']) {
        drawer.renderContents(cart, { open: false });
      }
      const page = document.querySelector('cart-items:not(cart-drawer-items)');
      if (page && typeof page.renderContents === 'function' && cart.sections && cart.sections['main-cart-items']) {
        page.renderContents(cart, null, null);
      }
      document.dispatchEvent(new CustomEvent('afterUpdateQuantity', {
        detail: { source: 'single-product-cart', cartData: cart }
      }));
    }

    bindEssential(attempt = 0) {
      if (this.essentialBound) return;
      const api = window.essentialCartApi;
      if (api && typeof api.onCartUpdate === 'function') {
        api.onCartUpdate(() => this.schedule());
        this.essentialBound = true;
      } else if (attempt < 40) {
        window.setTimeout(() => this.bindEssential(attempt + 1), 250);
      }
    }

    observeInputs() {
      if (!window.MutationObserver || !document.documentElement) return;
      new MutationObserver((mutations) => mutations.forEach((mutation) =>
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            this.enforceInputs(node);
            this.hideCartQuantityControls(node);
            this.scheduleRecommendationFilter();
          }
        })
      )).observe(document.documentElement, { childList: true, subtree: true });
    }

    enforceInputs(root) {
      const inputs = [];
      if (root.matches && root.matches(INPUTS)) inputs.push(root);
      if (root.querySelectorAll) inputs.push(...root.querySelectorAll(INPUTS));
      inputs.forEach((input) => {
        input.max = '1';
        input.setAttribute('max', '1');
        input.dataset.max = '1';
        if (Number(input.value || 0) > 1) input.value = '1';
        const scope = input.closest('quantity-input, [data-essential-cart-element="line-item"]') || input.parentElement;
        const plus = scope && scope.querySelector('button[name="plus"], [data-essential-cart-element="quantity-increment"]');
        if (plus) {
          const atLimit = Number(input.value || 0) >= 1;
          plus.classList.toggle('disabled', atLimit);
          plus.setAttribute('aria-disabled', atLimit ? 'true' : 'false');
        }
      });
    }

    hideCartQuantityControls(root) {
      const selector = [
        'cart-items quantity-input',
        'cart-drawer-items quantity-input',
        '#essential-cart-drawer-portal [data-essential-cart-element="quantity-select"]'
      ].join(',');
      const controls = [];
      if (root.matches && root.matches(selector)) controls.push(root);
      if (root.querySelectorAll) controls.push(...root.querySelectorAll(selector));
      controls.forEach((control) => {
        control.hidden = true;
        control.setAttribute('aria-hidden', 'true');
        control.style.setProperty('display', 'none', 'important');
      });
    }

    scheduleRecommendationFilter() {
      if (!this.ownedProducts.size || this.recommendationFilterPending) return;
      this.recommendationFilterPending = true;
      Promise.resolve().then(() => {
        this.recommendationFilterPending = false;
        this.filterOwnedRecommendations();
      });
    }

    filterOwnedRecommendations() {
      if (!this.ownedProducts.size || !document.querySelectorAll) return;
      const cards = document.querySelectorAll('#essential-cart-drawer-portal [data-essential-cart-element="upsell-product"]');
      cards.forEach((card) => {
        const handle = productHandleFromCard(card);
        if (!handle || !this.ownedProducts.has(handle)) return;
        const carousel = card.closest('[data-essential-cart-element="upsell-products-carousel"]');
        const wrapper = card.parentElement && carousel && card.parentElement !== carousel ? card.parentElement : card;
        wrapper.hidden = true;
        wrapper.setAttribute('aria-hidden', 'true');
        wrapper.style.setProperty('display', 'none', 'important');
        wrapper.dataset.dgmOwnedProduct = handle;
      });

      document.querySelectorAll('#essential-cart-drawer-portal section[data-essential-cart-element="upsell-products"]').forEach((section) => {
        const recommendationCards = section.querySelectorAll('[data-essential-cart-element="upsell-product"]');
        const hasAvailableRecommendation = Array.from(recommendationCards).some((card) => {
          const wrapper = card.parentElement && card.parentElement.dataset.dgmOwnedProduct ? card.parentElement : card;
          return !wrapper.hidden;
        });
        section.hidden = recommendationCards.length > 0 && !hasAvailableRecommendation;
        section.style.setProperty('display', section.hidden ? 'none' : '', section.hidden ? 'important' : '');
      });
    }

    onClick(event) {
      const target = event.target && typeof event.target.closest === 'function' ? event.target : null;
      const plus = target && target.closest('button[name="plus"], [data-essential-cart-element="quantity-increment"]');
      if (!plus) return;
      const scope = plus.closest('quantity-input, [data-essential-cart-element="line-item"]') || plus.parentElement;
      const input = scope && scope.querySelector('input[type="number"]');
      if (!input || Number(input.value || 0) < 1) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.notify();
    }

    onChange(event) {
      const input = event.target;
      if (!input || !input.matches || !input.matches(INPUTS) || Number(input.value || 0) <= 1) return;
      input.value = '1';
      this.notify();
    }

    onCartChanged(event) {
      const cart = event && event.detail && event.detail.cartData;
      if (cart && Array.isArray(cart.items)) this.remember(cart);
      this.schedule();
    }

    notify() {
      if (typeof window.pushSuccessMessage === 'function') window.pushSuccessMessage(MESSAGE);
      else document.dispatchEvent(new CustomEvent('singleProductCart:limitReached', { detail: { message: MESSAGE } }));
    }
  }

  window.SingleProductCart = SingleProductCart;
  window.singleProductCartTest = { uniqueUpdates, ownedProductHandles, productHandleFromCard };
  window.singleProductCart = new SingleProductCart();
})();
